import React, { useState } from "react";
import {
  Badge,
  Box,
  Card,
  Checkbox,
  Code,
  Container,
  createListCollection,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  Link,
  Select,
  SimpleGrid,
  Slider,
  Stack,
  Switch,
  Textarea,
  Text,
} from "@chakra-ui/react";
import {
  ColorModeButton,
  useColorModeValue,
} from "../../../components/ui/color-mode";
import { Button } from "../../../components/ui/Button";
import {
  LuArrowRight,
  LuBookOpen,
  LuCheck,
  LuClipboardCopy,
  LuLayoutGrid,
  LuPalette,
  LuPencilRuler,
  LuSparkles,
  LuType,
} from "react-icons/lu";

const sectionIds = {
  typography: "typography",
  buttons: "buttons",
  colors: "colors",
  forms: "forms",
  cards: "cards",
  home: "home",
  tokens: "tokens",
  guidelines: "guidelines",
};

const brandColors = [
  { name: "Arawaks Red", hex: "#E53935", note: "Primary action" },
  { name: "Deep Red", hex: "#B71C1C", note: "Pressed depth" },
  { name: "Soft Red", hex: "#FFEBEE", note: "Soft backgrounds" },
  { name: "Jungle Green", hex: "#4CAF50", note: "Success" },
  { name: "Sky Blue", hex: "#29B6F6", note: "Info" },
  { name: "Adventure Orange", hex: "#FF8A3D", note: "Warning" },
  { name: "Sun Yellow", hex: "#FFD54F", note: "Highlights" },
  { name: "Grape Purple", hex: "#8E5CF7", note: "Special moments" },
];

const neutralColors = [
  { name: "Warm White", hex: "#FFF8F5", note: "Canvas" },
  { name: "White", hex: "#FFFFFF", note: "Panels" },
  { name: "Charcoal", hex: "#2B2B2B", note: "Primary text" },
  { name: "Muted Gray", hex: "#6B7280", note: "Secondary text" },
  { name: "Soft Gray", hex: "#F3F4F6", note: "Separators" },
  { name: "Border Gray", hex: "#E5E7EB", note: "Borders" },
];

const fontWeights = [
  { label: "ExtraThin", value: 200 },
  { label: "Thin", value: 300 },
  { label: "Regular", value: 400 },
  { label: "Medium", value: 500 },
  { label: "SemiBold", value: 600 },
  { label: "Bold", value: 700 },
  { label: "ExtraBold", value: 800 },
  { label: "Black", value: 900 },
];

const fredokaSupportedWeights = [300, 400, 500, 600, 700];
const nunitoSupportedWeights = [200, 300, 400, 500, 600, 700, 800, 900];

const fredokaWeights = fontWeights.filter((w) => fredokaSupportedWeights.includes(w.value));
const nunitoWeights = fontWeights.filter((w) => nunitoSupportedWeights.includes(w.value));

const typeRoles = [
  {
    role: "Display Hero",
    token: "fontSizes.display",
    font: "display",
    weight: "700",
    usage: "Main landing heroes, campaign moments",
    sample: "Tu aventura empieza aqui",
  },
  {
    role: "H1 / Section Title",
    token: "fontSizes.section",
    font: "display",
    weight: "700",
    usage: "Major section headings in dashboard and marketing",
    sample: "Proximos eventos",
  },
  {
    role: "H2 / Card Title",
    token: "2xl",
    font: "display",
    weight: "600",
    usage: "Card headers and prominent panel titles",
    sample: "Hike en Chipinque",
  },
  {
    role: "Body Default",
    token: "md",
    font: "body",
    weight: "500",
    usage: "General paragraphs and dashboard descriptions",
    sample: "Exploracion guiada en la sierra con papas.",
  },
  {
    role: "Body Dense",
    token: "sm",
    font: "bodyAlt",
    weight: "500",
    usage: "Data-heavy cards and helper copy",
    sample: "28 socios activos y 78% asistencia",
  },
  {
    role: "Label",
    token: "xs",
    font: "display",
    weight: "700",
    usage: "Inputs, chips, uppercase labels",
    sample: "Nivel - 6to",
  },
  {
    role: "Caption",
    token: "xs",
    font: "bodyAlt",
    weight: "600",
    usage: "Timestamps, metadata, subtle info",
    sample: "Actualizado hace 2 horas",
  },
];

const buttonVariants = [
  { variant: "solid", label: "Solid", icon: LuSparkles },
  { variant: "outline", label: "Outline", icon: LuBookOpen },
  { variant: "surface", label: "Surface", icon: LuLayoutGrid },
  { variant: "ghost", label: "Ghost", icon: LuType },
];

const availableColors = [
  { label: "Red", value: "red", bg: "red.600" },
  { label: "Dark", value: "dark", bg: "gray.900" },
  { label: "White", value: "white", bg: "white", text: "gray.900", border: "gray.200" },
  { label: "Success", value: "success", bg: "green.500" },
  { label: "Info", value: "info", bg: "blue.500" },
  { label: "Warning", value: "warning", bg: "orange.400" },
  { label: "Danger", value: "danger", bg: "red.700" },
  { label: "Important", value: "important", bg: "purple.500" },
  { label: "Teal", value: "teal", bg: "teal.500" },
  { label: "Blue", value: "blue", bg: "blue.500" },
  { label: "Orange", value: "orange", bg: "orange.500" },
  { label: "Yellow", value: "yellow", bg: "yellow.400", text: "gray.900" },
  { label: "Purple", value: "purple", bg: "purple.500" },
  { label: "Pink", value: "pink", bg: "pink.500" },
];

const colorShades = ["50", "100", "200", "300", "400", "500", "600", "700", "800", "900", "950"];

const chakraPalettes = [
  "gray",
  "red",
  "orange",
  "yellow",
  "green",
  "teal",
  "blue",
  "cyan",
  "purple",
  "pink",
];

const customColorTokens = [
  { token: "brand.bg.pane", source: "gray.800" },
  { token: "brand.bg.surface", source: "gray.700" },
  { token: "brand.bg.detail", source: "gray.600" },
];

const customSemanticColorGroups = [
  {
    title: "Brand semantic",
    tokens: [
      { token: "brand.red", light: "#da2c27", dark: "#da2c27" },
      { token: "brand.redFg", light: "#b92722", dark: "#f0625b" },
      { token: "brand.redSubtle", light: "#f9e3e1", dark: "#4a2629" },
      { token: "brand.redBorder", light: "#e7a09b", dark: "#814047" },
      { token: "brand.redHover", light: "#c72521", dark: "#c72521" },
      { token: "brand.redActive", light: "#a3211d", dark: "#a3211d" },
    ],
  },
  {
    title: "Public and onboarding",
    tokens: [
      { token: "public.bg.canvas", light: "#f1f3f6", dark: "#171a21" },
      { token: "public.bg.panel", light: "#f8f9fb", dark: "#222733" },
      { token: "public.bg.surface", light: "#eef1f5", dark: "#292f3b" },
      { token: "public.bg.muted", light: "#e6eaf0", dark: "#303745" },
      { token: "public.bg.elevated", light: "#f8f9fb", dark: "#252b36" },
      { token: "public.fg.default", light: "#2d3748", dark: "#edf0f5" },
      { token: "public.fg.muted", light: "#5d6675", dark: "#b8c0ce" },
      { token: "public.fg.subtle", light: "#7a8494", dark: "#8f99a9" },
      { token: "public.border.default", light: "#cbd2dc", dark: "#485164" },
      { token: "public.border.subtle", light: "#dfe3e9", dark: "#343c4a" },
    ],
  },
  {
    title: "Design canvas",
    tokens: [
      { token: "design.bg.canvas", light: "orange.50", dark: "gray.950" },
      { token: "design.bg.panel", light: "white", dark: "gray.900" },
      { token: "design.bg.surface", light: "white", dark: "gray.800" },
      { token: "design.bg.muted", light: "gray.50", dark: "gray.700" },
      { token: "design.bg.elevated", light: "white", dark: "gray.900" },
      { token: "design.border.default", light: "gray.200", dark: "gray.700" },
      { token: "design.border.subtle", light: "gray.100", dark: "gray.800" },
    ],
  },
  {
    title: "Design foreground",
    tokens: [
      { token: "design.fg.default", light: "gray.800", dark: "gray.100" },
      { token: "design.fg.muted", light: "gray.600", dark: "gray.400" },
      { token: "design.fg.subtle", light: "gray.500", dark: "gray.500" },
      { token: "design.fg.inverse", light: "white", dark: "gray.950" },
      { token: "design.overlay.image", light: "rgba(10,0,0,0.70)", dark: "rgba(10,0,0,0.70)" },
    ],
  },
  {
    title: "Dashboard surfaces",
    tokens: [
      { token: "dashboard.bg.canvas", light: "gray.50", dark: "dashboard tint 950" },
      { token: "dashboard.bg.panel", light: "white", dark: "dashboard tint 900" },
      { token: "dashboard.bg.surface", light: "white", dark: "dashboard tint 900/800" },
      { token: "dashboard.bg.surfaceMuted", light: "gray.100", dark: "dashboard tint 800/700" },
      { token: "dashboard.bg.surfaceSubtle", light: "gray.50", dark: "dashboard tint 700/600" },
      { token: "dashboard.bg.dialog", light: "white", dark: "dashboard tint 900/800" },
      { token: "dashboard.bg.input", light: "white", dark: "dashboard tint 800/700" },
      { token: "dashboard.bg.popover", light: "white", dark: "dashboard tint 800/700" },
      { token: "dashboard.bg.tabList", light: "gray.100", dark: "dashboard tint 800/700" },
      { token: "dashboard.bg.tabHover", light: "blackAlpha.50", dark: "whiteAlpha.200" },
      { token: "dashboard.bg.tabSelected", light: "red.100", dark: "dashboard tint 700/600" },
      { token: "dashboard.bg.choiceSelected", light: "gray.200", dark: "dashboard tint 700/600" },
    ],
  },
  {
    title: "Dashboard foreground",
    tokens: [
      { token: "dashboard.fg.default", light: "gray.800", dark: "gray.200" },
      { token: "dashboard.fg.muted", light: "gray.600", dark: "gray.400" },
      { token: "dashboard.fg.subtle", light: "gray.500", dark: "gray.500" },
      { token: "dashboard.fg.onAccent", light: "white", dark: "white" },
      { token: "dashboard.border.default", light: "gray.200", dark: "dashboard tint 600/500" },
      { token: "dashboard.border.muted", light: "gray.100", dark: "dashboard tint 700/600" },
    ],
  },
  {
    title: "Dashboard accents",
    tokens: [
      { token: "dashboard.accent.solid", light: "red.600", dark: "red.400" },
      { token: "dashboard.accent.fg", light: "red.600", dark: "red.200" },
      { token: "dashboard.accent.subtle", light: "red.100", dark: "red.900" },
      { token: "dashboard.accent.border", light: "red.600", dark: "red.300" },
      { token: "dashboard.feedback.error", light: "red.600", dark: "red.300" },
    ],
  },
  {
    title: "Dashboard tags",
    tokens: [
      { token: "dashboard.tag.bg", light: "gray.100", dark: "dashboard tint 700/600" },
      { token: "dashboard.tag.fg", light: "gray.700", dark: "gray.100/white" },
      { token: "dashboard.tag.border", light: "gray.300", dark: "dashboard tint 500/400" },
    ],
  },
  {
    title: "Dashboard metrics",
    tokens: [
      { token: "dashboard.metric.info.bg", light: "arawaks.skyBlue", dark: "arawaks.skyBlue" },
      { token: "dashboard.metric.info.fg", light: "white", dark: "white" },
      { token: "dashboard.metric.info.icon", light: "white", dark: "white" },
      { token: "dashboard.metric.success.bg", light: "arawaks.jungleGreen", dark: "arawaks.jungleGreen" },
      { token: "dashboard.metric.success.fg", light: "white", dark: "white" },
      { token: "dashboard.metric.success.icon", light: "white", dark: "white" },
      { token: "dashboard.metric.warning.bg", light: "arawaks.adventureOrange", dark: "arawaks.adventureOrange" },
      { token: "dashboard.metric.warning.fg", light: "white", dark: "white" },
      { token: "dashboard.metric.warning.icon", light: "white", dark: "white" },
      { token: "dashboard.metric.danger.bg", light: "arawaks.campfireRed", dark: "arawaks.campfireRed" },
      { token: "dashboard.metric.danger.fg", light: "white", dark: "white" },
      { token: "dashboard.metric.danger.icon", light: "white", dark: "white" },
    ],
  },
];

const homePatterns = [
  {
    title: "Hero video",
    role: "First viewport introduction",
    tokens: ["brand.red", "whiteAlpha.900", "transparent button"],
    notes: "Full-bleed media, fixed overlay, one solid CTA and one transparent ghost CTA. Keep the current video, no slideshow.",
  },
  {
    title: "Section heading",
    role: "Marketing section intro",
    tokens: ["brand.redSubtle", "brand.redFg", "display font"],
    notes: "Eyebrow pill plus display heading. Center only when the whole section is centered.",
  },
  {
    title: "Image story card",
    role: "Value cards and visual promises",
    tokens: ["design.overlay.image", "card radius", "cardLift shadow"],
    notes: "Image zooms on hover, card lifts slightly, text sits over the lower gradient.",
  },
  {
    title: "Dark image band",
    role: "History and CTA sections",
    tokens: ["design.overlay.image", "whiteAlpha text", "transparent button"],
    notes: "Background image stays visible through the standard image overlay. Use transparent ghost buttons on dark media.",
  },
];

const labelStyle = {
  fontSize: "11px",
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};

const gradeCollection = createListCollection({
  items: [
    { label: "4to", value: "4to" },
    { label: "5to", value: "5to" },
    { label: "6to", value: "6to" },
    { label: "7mo", value: "7mo" },
    { label: "8vo", value: "8vo" },
    { label: "Todos", value: "todos" },
  ],
});

const SectionTitle = ({ id, eyebrow, title, description, icon }) => (
  <Stack id={id} scrollMarginTop="32" gap="4" mb="7">
    <HStack gap="3">
      <Box p="2.5" rounded="full" bg="design.bg.muted" borderWidth="1px" borderColor="design.border.subtle">
        <Icon as={icon} boxSize="4" color="brand.redFg" />
      </Box>
      <Text sx={labelStyle} color="brand.redFg">
        {eyebrow}
      </Text>
    </HStack>
    <Heading fontFamily="display" fontSize={{ base: "2xl", md: "3xl" }} color="design.fg.default">
      {title}
    </Heading>
    <Text maxW="4xl" color="design.fg.muted" fontSize="md" lineHeight="1.8">
      {description}
    </Text>
  </Stack>
);

const SectionFrame = ({ children }) => (
  <Card.Root
    bg="design.bg.panel"
    borderWidth="1px"
    borderColor="design.border.default"
    rounded="stage"
    shadow="0 12px 28px rgba(0,0,0,0.08)"
  >
    <Card.Body p={{ base: 5, md: 8 }}>{children}</Card.Body>
  </Card.Root>
);

const CopySwatch = ({ item }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(item.hex);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box
      onClick={handleCopy}
      role="button"
      tabIndex={0}
      cursor="pointer"
      overflow="hidden"
      rounded="card"
      borderWidth="1px"
      borderColor="design.border.default"
      bg="design.bg.surface"
      transition="transform 140ms ease, box-shadow 140ms ease"
      _hover={{ transform: "translateY(-2px)", shadow: "0 14px 28px rgba(0,0,0,0.10)" }}
      _active={{ transform: "translateY(1px)" }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleCopy();
        }
      }}
    >
      <Box h="20" bg={item.hex} />
      <Stack gap="1.5" p="4">
        <Text fontFamily="display" fontSize="md" color="design.fg.default">
          {item.name}
        </Text>
        <Text fontSize="sm" color="design.fg.muted">
          {item.note}
        </Text>
        <HStack
          alignSelf="start"
          px="2.5"
          py="1"
          rounded="full"
          bg="design.bg.muted"
          borderWidth="1px"
          borderColor="design.border.subtle"
          color="design.fg.muted"
          fontSize="xs"
          fontWeight="700"
        >
          <Icon as={copied ? LuCheck : LuClipboardCopy} boxSize="3.5" />
          <Text>{copied ? "Copied" : item.hex}</Text>
        </HStack>
      </Stack>
    </Box>
  );
};

const TypographyWeights = ({ title, family, weights }) => {
  return (
    <Card.Root bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card">
      <Card.Body gap="4" p="5">
        <Text sx={labelStyle} color="design.fg.muted">
          {title}
        </Text>
        <Stack gap="3">
          {weights.map((w) => (
            <Box key={`${title}-${w.value}`} p="3.5" rounded="soft" bg="design.bg.muted" borderWidth="1px" borderColor="design.border.subtle">
              <HStack justify="space-between" mb="1.5">
                <Text fontSize="sm" color="design.fg.muted">{w.label}</Text>
                <Code>{w.value}</Code>
              </HStack>
              <Text
                fontFamily={family}
                fontWeight={String(w.value)}
                fontSize="xl"
                lineHeight="1.2"
              >
                Arawaks Adventure System
              </Text>
            </Box>
          ))}
        </Stack>
      </Card.Body>
    </Card.Root>
  );
};

const ColorRamp = ({ palette }) => (
  <Card.Root bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card">
    <Card.Body p={{ base: 4, md: 5 }} gap="3">
      <HStack justify="space-between" gap="3" wrap="wrap">
        <Text fontFamily="display" fontSize="lg" fontWeight="700" color="design.fg.default" textTransform="capitalize">
          {palette}
        </Text>
        <Code>{palette}.50 - {palette}.950</Code>
      </HStack>
      <SimpleGrid columns={{ base: 2, sm: 3, md: 6, xl: 11 }} gap="2">
        {colorShades.map((shade) => {
          const token = `${palette}.${shade}`;
          const shadeNumber = Number(shade);
          const textColor = shadeNumber >= 500 ? "white" : "gray.950";

          return (
            <Box
              key={token}
              minH="82px"
              p="2.5"
              rounded="soft"
              bg={token}
              color={textColor}
              borderWidth="1px"
              borderColor={shadeNumber <= 100 ? "design.border.default" : "transparent"}
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Text fontSize="xs" fontWeight="800">
                {shade}
              </Text>
              <Text fontSize="10px" fontWeight="700" lineHeight="1.2">
                {token}
              </Text>
            </Box>
          );
        })}
      </SimpleGrid>
    </Card.Body>
  </Card.Root>
);

const TokenPreview = ({ token, label }) => {
  const canRenderToken = token && !token.includes(" ") && !token.includes("/");

  return (
    <Box
      h="16"
      rounded="soft"
      bg={canRenderToken ? token : "design.bg.muted"}
      borderWidth="1px"
      borderColor="design.border.default"
      position="relative"
      overflow="hidden"
    >
      <Box position="absolute" inset="0" bg="linear-gradient(to bottom, transparent, rgba(0,0,0,0.08))" />
      {label ? (
        <Text
          position="absolute"
          left="2.5"
          bottom="2"
          fontSize="10px"
          fontWeight="800"
          color={canRenderToken ? "white" : "design.fg.muted"}
          textShadow={canRenderToken ? "0 1px 4px rgba(0,0,0,0.55)" : "none"}
        >
          {label}
        </Text>
      ) : null}
    </Box>
  );
};

const CustomTokenCard = ({ item }) => (
  <Card.Root bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card">
    <Card.Body p="4" gap="3">
      <TokenPreview token={item.token} />
      <Stack gap="1">
        <Code alignSelf="start">{item.token}</Code>
        <Text fontSize="xs" color="design.fg.muted">
          source: {item.source}
        </Text>
      </Stack>
    </Card.Body>
  </Card.Root>
);

const SemanticTokenCard = ({ item }) => (
  <Card.Root bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card">
    <Card.Body p="4" gap="3">
      <TokenPreview token={item.token} label="current" />
      <SimpleGrid columns={2} gap="2">
        <Stack gap="1">
          <TokenPreview token={item.light} label="light" />
          <Text fontSize="10px" color="design.fg.muted" fontWeight="700">
            {item.light}
          </Text>
        </Stack>
        <Stack gap="1">
          <TokenPreview token={item.dark} label="dark" />
          <Text fontSize="10px" color="design.fg.muted" fontWeight="700">
            {item.dark}
          </Text>
        </Stack>
      </SimpleGrid>
      <Code alignSelf="start">{item.token}</Code>
    </Card.Body>
  </Card.Root>
);

const PatternSpecCard = ({ pattern }) => (
  <Card.Root bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card">
    <Card.Body p="5" gap="4">
      <Stack gap="1">
        <Heading fontFamily="display" fontSize="xl">
          {pattern.title}
        </Heading>
        <Text fontSize="sm" color="design.fg.muted" fontWeight="700">
          {pattern.role}
        </Text>
      </Stack>
      <Text color="design.fg.muted" lineHeight="1.7">
        {pattern.notes}
      </Text>
      <HStack gap="2" wrap="wrap">
        {pattern.tokens.map((token) => (
          <Code key={token}>{token}</Code>
        ))}
      </HStack>
    </Card.Body>
  </Card.Root>
);

const EventCard = () => (
  <Card.Root
    variant="elevated"
    overflow="hidden"
    borderWidth="1px"
    borderColor="design.border.default"
    bg="design.bg.surface"
    rounded="card"
    shadow="cardLift"
  >
    <Box minH="52" bgGradient="linear(135deg, red.500 0%, red.700 100%)" position="relative" overflow="hidden">
      <Box
        position="absolute"
        inset="0"
        bgImage="radial-gradient(circle at 20% 20%, rgba(255,255,255,0.22) 0, transparent 22%), radial-gradient(circle at 84% 14%, rgba(255,255,255,0.16) 0, transparent 18%), radial-gradient(circle at 70% 82%, rgba(255,255,255,0.10) 0, transparent 14%)"
      />
      <Box position="absolute" inset="0" bg="linear-gradient(to top, rgba(0,0,0,0.58), transparent 58%)" />
      <Stack position="absolute" inset="0" justify="end" p="6" color="white" gap="2.5">
        <Badge alignSelf="start" colorPalette="yellow" variant="solid" rounded="full" px="3" py="1">
          Proximo evento
        </Badge>
        <Heading fontFamily="display" fontSize={{ base: "2xl", md: "3xl" }}>
          Hike en Chipinque Padre e Hijo
        </Heading>
        <Text opacity="0.86" maxW="lg">
          Card de referencia para el patron de "Proximos eventos" con jerarquia visual clara y aire.
        </Text>
      </Stack>
    </Box>

    <Card.Body gap="4" p="6">
      <HStack justify="space-between" gap="3" flexWrap="wrap">
        <HStack gap="2">
          <Badge colorPalette="green" variant="subtle" rounded="full">Naturaleza</Badge>
          <Badge colorPalette="red" variant="outline" rounded="full">24 socios</Badge>
        </HStack>
        <Text fontSize="sm" color="design.fg.muted">02 may 2026 · 09:00 - 12:00</Text>
      </HStack>
      <Text color="design.fg.default" lineHeight="1.8">
        Exploracion guiada en la sierra con papas. Este bloque deberia usarse cuando la tarjeta necesita protagonismo editorial.
      </Text>
    </Card.Body>

    <Card.Footer p="6" pt="0" justifyContent="space-between" gap="3" flexWrap="wrap">
      <Button variant="outline" color="red" size="sm">Ver detalles</Button>
      <Button variant="solid" color="red" size="sm">Confirmar asistencia</Button>
    </Card.Footer>
  </Card.Root>
);

const FormsShowcase = () => {
  const [checked, setChecked] = useState(true);
  const [notifications, setNotifications] = useState(false);
  const [difficulty, setDifficulty] = useState([55]);
  const [grade, setGrade] = useState(["6to"]);

  return (
    <Stack gap="6">
      <SimpleGrid columns={{ base: 1, xl: 2 }} gap="6">
        <Card.Root bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card">
          <Card.Body p={{ base: 5, md: 6 }} gap="5">
            <Text sx={labelStyle} color="design.fg.muted">Checkbox + Switch</Text>

            <Checkbox.Root
              checked={checked}
              onCheckedChange={(e) => setChecked(!!e.checked)}
              alignItems="center"
              gap="3"
              p="3"
              rounded="soft"
              borderWidth="1px"
              borderColor="design.border.subtle"
              bg="design.bg.muted"
              transition="all 160ms ease"
              _hover={{ borderColor: "red.200", bg: "red.50" }}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control
                transition="all 160ms ease"
                borderWidth="2px"
                borderColor="red.400"
                bg="white"
                rounded="md"
                boxShadow="0 3px 0 0 #f7c9c8"
                _active={{ transform: "translateY(1px)", boxShadow: "0 2px 0 0 #f7c9c8" }}
                _checked={{
                  bg: "red.600",
                  borderColor: "red.700",
                  color: "white",
                  transform: "translateY(1px)",
                  boxShadow: "0 2px 0 0 #b71c1c",
                }}
              />
              <Checkbox.Label fontWeight="700" color="design.fg.default">
                Llevar botiquin para actividad
              </Checkbox.Label>
            </Checkbox.Root>

            <HStack
              justify="space-between"
              p="3.5"
              rounded="soft"
              bg="design.bg.muted"
              borderWidth="1px"
              borderColor="design.border.subtle"
              transition="all 160ms ease"
              _hover={{ borderColor: "red.200", bg: "red.50" }}
            >
              <Stack gap="0.5">
                <Text fontFamily="display" fontSize="md">Notificaciones</Text>
                <Text color="design.fg.muted" fontSize="sm">Avisar cambios de horario</Text>
              </Stack>
              <Switch.Root checked={notifications} onCheckedChange={(e) => setNotifications(!!e.checked)}>
                <Switch.HiddenInput />
                <Switch.Control
                  transition="all 180ms ease"
                  bg={notifications ? "red.600" : "gray.300"}
                  borderWidth="1px"
                  borderColor={notifications ? "red.700" : "gray.400"}
                  boxShadow={notifications ? "0 0 0 4px rgba(229,57,53,0.14)" : "none"}
                  _hover={{ bg: notifications ? "red.500" : "gray.400" }}
                  _active={{ transform: "translateY(1px)" }}
                >
                  <Switch.Thumb
                    transition="all 180ms ease"
                    boxShadow="0 2px 6px rgba(0,0,0,0.2)"
                    _checked={{ boxShadow: "0 3px 8px rgba(183,28,28,0.35)" }}
                  />
                </Switch.Control>
              </Switch.Root>
            </HStack>
          </Card.Body>
        </Card.Root>

        <Card.Root bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card">
          <Card.Body p={{ base: 5, md: 6 }} gap="5">
            <Text sx={labelStyle} color="design.fg.muted">Slider + Dropdown</Text>

            <Stack gap="3">
              <HStack justify="space-between">
                <Text fontFamily="display" fontSize="md">Intensidad de actividad</Text>
                <Badge rounded="full" colorPalette="red" variant="subtle">{difficulty[0]}%</Badge>
              </HStack>

              <Slider.Root
                value={difficulty}
                onValueChange={(e) => setDifficulty(e.value)}
                min={0}
                max={100}
              >
                <Slider.Control>
                  <Slider.Track
                    h="3"
                    rounded="full"
                    bg="gray.200"
                    transition="all 160ms ease"
                    _hover={{ bg: "gray.300" }}
                  >
                    <Slider.Range
                      rounded="full"
                      bg="linear-gradient(90deg, #e53935 0%, #ff8a3d 100%)"
                    />
                  </Slider.Track>
                  <Slider.Thumbs
                    boxSize="6"
                    borderWidth="2px"
                    borderColor="white"
                    bg="red.600"
                    boxShadow="0 4px 0 0 #b71c1c"
                    transition="transform 160ms ease, background-color 160ms ease, box-shadow 160ms ease"
                    _hover={{ transform: "translateY(-1px) scale(1.04)", bg: "red.500" }}
                    _active={{ transform: "translateY(1px)", boxShadow: "0 2px 0 0 #b71c1c" }}
                    _focusVisible={{ boxShadow: "0 0 0 4px rgba(229,57,53,0.22)" }}
                  />
                </Slider.Control>
              </Slider.Root>
            </Stack>

            <Stack gap="2">
              <Text fontFamily="display" fontSize="md">Grado objetivo</Text>
              <Select.Root
                collection={gradeCollection}
                value={grade}
                onValueChange={(e) => setGrade(e.value)}
                positioning={{ strategy: "fixed", hideWhenDetached: true }}
              >
                <Select.HiddenSelect />
                <Select.Control>
                  <Select.Trigger
                    h="11"
                    px="4"
                    rounded="pill"
                    bg="white"
                    borderWidth="2px"
                    borderColor="gray.200"
                    fontWeight="700"
                    transition="all 160ms ease"
                    _hover={{ borderColor: "red.300", bg: "red.50" }}
                    _focusVisible={{ borderColor: "red.500", boxShadow: "0 0 0 4px rgba(229,57,53,0.15)" }}
                  >
                    <Select.ValueText placeholder="Selecciona un grado" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator color="red.500" />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content
                    rounded="xl"
                    borderWidth="1px"
                    borderColor="red.100"
                    boxShadow="0 20px 38px rgba(183,28,28,0.14)"
                    p="1.5"
                    bg="white"
                  >
                    {gradeCollection.items.map((item) => (
                      <Select.Item
                        item={item}
                        key={item.value}
                        rounded="lg"
                        px="3"
                        py="2"
                        fontWeight="700"
                        color="design.fg.default"
                        transition="all 130ms ease"
                        _highlighted={{ bg: "red.50", color: "red.700" }}
                        _selected={{ bg: "red.100", color: "red.700" }}
                      >
                        {item.label}
                        <Select.ItemIndicator>
                          <Icon as={LuCheck} boxSize="4" />
                        </Select.ItemIndicator>
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            </Stack>
          </Card.Body>
        </Card.Root>
      </SimpleGrid>

      <Card.Root bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card">
        <Card.Body p={{ base: 5, md: 6 }} gap="5">
          <Text sx={labelStyle} color="design.fg.muted">Text Inputs</Text>

          <SimpleGrid columns={{ base: 1, md: 2 }} gap="4">
            <Stack gap="2">
              <Text fontFamily="display" fontSize="sm" color="design.fg.muted">Titulo de actividad</Text>
              <Input
                placeholder="Ej. Campamento en la Huasteca"
                h="11"
                px="4"
                rounded="pill"
                bg="white"
                borderWidth="2px"
                borderColor="gray.200"
                fontWeight="600"
                transition="all 160ms ease"
                _hover={{ borderColor: "red.300", bg: "red.50" }}
                _focusVisible={{ borderColor: "red.500", boxShadow: "0 0 0 4px rgba(229,57,53,0.15)" }}
              />
            </Stack>

            <Stack gap="2">
              <Text fontFamily="display" fontSize="sm" color="design.fg.muted">Contacto responsable</Text>
              <Input
                placeholder="correo@arawaks.mx"
                type="email"
                h="11"
                px="4"
                rounded="pill"
                bg="white"
                borderWidth="2px"
                borderColor="gray.200"
                fontWeight="600"
                transition="all 160ms ease"
                _hover={{ borderColor: "red.300", bg: "red.50" }}
                _focusVisible={{ borderColor: "red.500", boxShadow: "0 0 0 4px rgba(229,57,53,0.15)" }}
              />
            </Stack>
          </SimpleGrid>

          <Stack gap="2">
            <Text fontFamily="display" fontSize="sm" color="design.fg.muted">Notas para el equipo</Text>
            <Textarea
              placeholder="Indicaciones adicionales, ruta, puntos de encuentro y material recomendado."
              minH="128px"
              rounded="2xl"
              bg="white"
              borderWidth="2px"
              borderColor="gray.200"
              fontWeight="500"
              lineHeight="1.7"
              transition="all 160ms ease"
              _hover={{ borderColor: "red.300", bg: "red.50" }}
              _focusVisible={{ borderColor: "red.500", boxShadow: "0 0 0 4px rgba(229,57,53,0.15)" }}
            />
          </Stack>
        </Card.Body>
      </Card.Root>
    </Stack>
  );
};

const DesignSystem = () => {
  const [selectedColor, setSelectedColor] = useState("red");
  const canvasBg = useColorModeValue("design.bg.canvas", "design.bg.canvas");
  const heroBg = useColorModeValue(
    "linear-gradient(140deg, rgba(229,57,53,0.15) 0%, rgba(255,138,61,0.10) 35%, rgba(255,248,245,0.97) 100%)",
    "linear-gradient(140deg, rgba(229,57,53,0.20) 0%, rgba(35,35,35,0.92) 45%, rgba(17,24,39,0.98) 100%)",
  );

  return (
    <Box minH="100vh" bg={canvasBg} color="design.fg.default" py={{ base: 6, md: 10 }}>
      <Container maxW="8xl">
        <Stack gap={{ base: 7, md: 10 }}>
          <SectionFrame>
            <Stack gap="7">
              <Flex justify="space-between" align={{ base: "start", md: "center" }} direction={{ base: "column", md: "row" }} gap="4">
                <HStack gap="3" wrap="wrap">
                  <Badge colorPalette="red" variant="solid" rounded="full" px="3" py="1.5">Design harness</Badge>
                  <Badge colorPalette="green" variant="subtle" rounded="full" px="3" py="1.5">Tokens + recipes</Badge>
                  <Badge colorPalette="orange" variant="subtle" rounded="full" px="3" py="1.5">Chakra v3</Badge>
                </HStack>
                <ColorModeButton />
              </Flex>

              <Card.Root rounded="card" bg={heroBg} borderWidth="1px" borderColor="design.border.default" overflow="hidden">
                <Card.Body p={{ base: 5, md: 8 }}>
                  <SimpleGrid columns={{ base: 1, lg: 2 }} gap={{ base: 6, md: 10 }}>
                    <Stack gap="4" justify="center">
                      <Heading fontFamily="display" fontSize="display" lineHeight="0.94">
                        Design System
                      </Heading>
                      <Text fontSize={{ base: "lg", md: "xl" }} lineHeight="1.8" color="design.fg.muted" maxW="2xl">
                        Espacio vivo para validar tipografia, estados de boton, cards y tokens antes de mover patrones al producto.
                      </Text>
                      <HStack gap="3" wrap="wrap">
                        <Button variant="solid" color="red" size="lg">
                          <Icon as={LuSparkles} boxSize="4" />
                          Solid pressed CTA
                        </Button>
                        <Button variant="outline" color="red" size="lg">
                          <Icon as={LuBookOpen} boxSize="4" />
                          Outline action
                        </Button>
                      </HStack>
                    </Stack>

                    <Card.Root bg="design.bg.panel" borderWidth="1px" borderColor="design.border.default" rounded="card">
                      <Card.Body gap="4" p={{ base: 5, md: 6 }}>
                        <Text sx={labelStyle} color="design.fg.muted">Quick map</Text>
                        <SimpleGrid columns={2} gap="2.5">
                          {Object.entries(sectionIds).map(([key, id]) => (
                            <Link
                              key={id}
                              href={`#${id}`}
                              px="3"
                              py="2"
                              rounded="soft"
                              bg="design.bg.muted"
                              borderWidth="1px"
                              borderColor="design.border.subtle"
                              fontWeight="700"
                              color="design.fg.default"
                              _hover={{ bg: "brand.redSubtle", color: "brand.redFg" }}
                              textTransform="capitalize"
                            >
                              {key}
                            </Link>
                          ))}
                        </SimpleGrid>
                        <Text fontSize="sm" color="design.fg.muted" lineHeight="1.7">
                          Button color is resolved once, then every variant derives its background, border, shadow, hover, and pressed states from that palette.
                        </Text>
                      </Card.Body>
                    </Card.Root>
                  </SimpleGrid>
                </Card.Body>
              </Card.Root>
            </Stack>
          </SectionFrame>

          <SectionFrame>
            <SectionTitle
              id={sectionIds.typography}
              eyebrow="Type system"
              icon={LuType}
              title="Typography"
              description="Fredoka is the expressive display face (native weights 300-700). Nunito Sans covers dense UI reading with 200-900. The role map below defines defaults for headings, body, labels, and captions."
            />

            <SimpleGrid columns={{ base: 1, xl: 2 }} gap="6" mb="7">
              <TypographyWeights title="Fredoka (display)" family="display" weights={fredokaWeights} />
              <TypographyWeights title="Nunito Sans (bodyAlt)" family="bodyAlt" weights={nunitoWeights} />
            </SimpleGrid>

            <Card.Root bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card">
              <Card.Body p={{ base: 5, md: 6 }} gap="4">
                <Text sx={labelStyle} color="design.fg.muted">Default usage map</Text>
                <Stack gap="3">
                  {typeRoles.map((row) => (
                    <SimpleGrid
                      key={row.role}
                      columns={{ base: 1, md: 5 }}
                      gap="3"
                      p="3.5"
                      rounded="soft"
                      bg="design.bg.muted"
                      borderWidth="1px"
                      borderColor="design.border.subtle"
                      alignItems="center"
                    >
                      <Stack gap="0.5">
                        <Text fontFamily="display" fontWeight="700">{row.role}</Text>
                        <Text fontSize="xs" color="design.fg.muted">{row.usage}</Text>
                      </Stack>
                      <Code>{row.token}</Code>
                      <Code>{row.font}</Code>
                      <Code>{row.weight}</Code>
                      <Text
                        fontFamily={row.font}
                        fontWeight={row.weight}
                        fontSize={row.role === "Display Hero" ? "2xl" : row.role.includes("H") ? "xl" : "sm"}
                      >
                        {row.sample}
                      </Text>
                    </SimpleGrid>
                  ))}
                </Stack>
              </Card.Body>
            </Card.Root>
          </SectionFrame>

          <SectionFrame>
            <SectionTitle
              id={sectionIds.buttons}
              eyebrow="Interaction"
              icon={LuArrowRight}
              title="Button Variants"
              description="Select a color palette, then compare how each variant renders. Press and hold to see the full active drop."
            />

            <Card.Root bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card" mb="6">
              <Card.Body p={{ base: 5, md: 6 }} gap="4">
                <Text sx={labelStyle} color="design.fg.muted">Select Color Palette</Text>
                <HStack gap="2" wrap="wrap">
                  {availableColors.map((colorItem) => (
                    <Button
                      key={colorItem.value}
                      variant={selectedColor === colorItem.value ? "solid" : "outline"}
                      color={colorItem.value}
                      size="sm"
                      onClick={() => setSelectedColor(colorItem.value)}
                    >
                      {colorItem.label}
                    </Button>
                  ))}
                </HStack>
              </Card.Body>
            </Card.Root>

            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap="4" mb="6">
              {buttonVariants.map((item) => (
                <Card.Root key={item.variant} bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card">
                  <Card.Body p="4" gap="3">
                    <Text sx={labelStyle} color="design.fg.muted">{item.label}</Text>
                    <Button variant={item.variant} color={selectedColor} size="md">
                      <Icon as={item.icon} boxSize="4" />
                      {item.label}
                    </Button>
                    <Text fontSize="xs" color="design.fg.muted">variant: <Code>{item.variant}</Code></Text>
                  </Card.Body>
                </Card.Root>
              ))}
            </SimpleGrid>

            <Card.Root bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card">
              <Card.Body p={{ base: 5, md: 6 }} gap="4">
                <Text sx={labelStyle} color="design.fg.muted">Sizes and behavior</Text>
                <HStack gap="3" wrap="wrap">
                  <Button variant="solid" color={selectedColor} size="sm">size sm</Button>
                  <Button variant="solid" color={selectedColor} size="md">size md</Button>
                  <Button variant="solid" color={selectedColor} size="lg">size lg</Button>
                </HStack>
                <HStack gap="3" wrap="wrap">
                  {buttonVariants.map((item) => (
                    <Button key={`disabled-${item.variant}`} variant={item.variant} color={selectedColor} size="sm" disabled>
                      {item.label} disabled
                    </Button>
                  ))}
                </HStack>
                <Text fontSize="sm" color="design.fg.muted" lineHeight="1.7">
                  Active state spec: solid and outline buttons translate down to meet their shadow. Surface and ghost stay flat and only shift color.
                </Text>
              </Card.Body>
            </Card.Root>
          </SectionFrame>

          <SectionFrame>
            <SectionTitle
              id={sectionIds.colors}
              eyebrow="Color ramps"
              icon={LuPalette}
              title="Chakra Colors"
              description="Full Chakra palette ramps from 50 through 950. Use these rows to compare button fills, outlines, shadows, subtle surfaces, and dark-mode contrast."
            />

            <Stack gap="4">
              {chakraPalettes.map((palette) => (
                <ColorRamp key={palette} palette={palette} />
              ))}
            </Stack>

            <Text sx={labelStyle} color="design.fg.muted" mt="8" mb="3">
              Custom token colors
            </Text>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap="3" mb="8">
              {customColorTokens.map((item) => (
                <CustomTokenCard key={item.token} item={item} />
              ))}
            </SimpleGrid>

            <Text sx={labelStyle} color="design.fg.muted" mb="3">
              Custom semantic colors
            </Text>
            <Stack gap="5">
              {customSemanticColorGroups.map((group) => (
                <Card.Root key={group.title} bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card">
                  <Card.Body p={{ base: 4, md: 5 }} gap="4">
                    <HStack justify="space-between" gap="3" wrap="wrap">
                      <Text fontFamily="display" fontSize="lg" fontWeight="700" color="design.fg.default">
                        {group.title}
                      </Text>
                      <Badge rounded="full" variant="subtle" colorPalette="red">
                        {group.tokens.length} tokens
                      </Badge>
                    </HStack>
                    <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} gap="3">
                      {group.tokens.map((item) => (
                        <SemanticTokenCard key={item.token} item={item} />
                      ))}
                    </SimpleGrid>
                  </Card.Body>
                </Card.Root>
              ))}
            </Stack>
          </SectionFrame>

          <SectionFrame>
            <SectionTitle
              id={sectionIds.forms}
              eyebrow="Form controls"
              icon={LuPencilRuler}
              title="Inputs"
              description="Custom controls for this visual language: rounded, tactile and smooth on hover, focus and triggered states."
            />

            <FormsShowcase />
          </SectionFrame>

          <SectionFrame>
            <SectionTitle
              id={sectionIds.cards}
              eyebrow="Surface patterns"
              icon={LuLayoutGrid}
              title="Cards"
              description="Reference card composition for event-heavy modules, especially the Proximos eventos pattern."
            />

            <SimpleGrid columns={{ base: 1, xl: 2 }} gap="6">
              <EventCard />
              <Card.Root bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card">
                <Card.Body p={{ base: 5, md: 6 }} gap="4">
                  <Text sx={labelStyle} color="design.fg.muted">Card anatomy checklist</Text>
                  <Stack gap="3">
                    {[
                      "Top media area with strong context",
                      "Title in display font and metadata in bodyAlt",
                      "Tags with concise state info",
                      "Dual action row with solid and outline hierarchy",
                    ].map((line) => (
                      <HStack key={line} align="start" gap="2.5">
                        <Icon as={LuCheck} mt="0.5" color="green.500" boxSize="4" />
                        <Text color="design.fg.muted" lineHeight="1.8">{line}</Text>
                      </HStack>
                    ))}
                  </Stack>
                </Card.Body>
              </Card.Root>
            </SimpleGrid>
          </SectionFrame>

          <SectionFrame>
            <SectionTitle
              id={sectionIds.home}
              eyebrow="Homepage patterns"
              icon={LuLayoutGrid}
              title="Home Page"
              description="Reusable patterns extracted from the Inicio page. These are the homepage-specific rules for media sections, value cards, and action blocks."
            />

            <SimpleGrid columns={{ base: 1, md: 2 }} gap="4">
              {homePatterns.map((pattern) => (
                <PatternSpecCard key={pattern.title} pattern={pattern} />
              ))}
            </SimpleGrid>
          </SectionFrame>

          <SectionFrame>
            <SectionTitle
              id={sectionIds.tokens}
              eyebrow="Visual language"
              icon={LuPalette}
              title="Tokens"
              description="Color, surface and typography tokens used by this page. Click swatches to copy values quickly."
            />

            <Text sx={labelStyle} color="design.fg.muted" mb="3">Brand palette</Text>
            <SimpleGrid columns={{ base: 2, md: 4 }} gap="3" mb="6">
              {brandColors.map((item) => (
                <CopySwatch key={item.hex} item={item} />
              ))}
            </SimpleGrid>

            <Text sx={labelStyle} color="design.fg.muted" mb="3">Neutral palette</Text>
            <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} gap="3">
              {neutralColors.map((item) => (
                <CopySwatch key={item.hex} item={item} />
              ))}
            </SimpleGrid>
          </SectionFrame>

          <SectionFrame>
            <SectionTitle
              id={sectionIds.guidelines}
              eyebrow="Usage rules"
              icon={LuPencilRuler}
              title="Guidelines"
              description="Short principles to keep implementation consistent as we scale this design system."
            />

            <SimpleGrid columns={{ base: 1, md: 2 }} gap="4">
              {[
                {
                  title: "Default to recipe variants",
                  copy: "Use shared variants before introducing one-off button styles.",
                },
                {
                  title: "Respect the role map",
                  copy: "Use display for hierarchy and body/bodyAlt for readability.",
                },
                {
                  title: "Prefer semantic tokens",
                  copy: "Use design.bg, design.fg, and brand role tokens instead of hardcoded values.",
                },
                {
                  title: "Keep spacing generous",
                  copy: "Use larger section gaps and avoid stacking too many controls into one card.",
                },
              ].map((item) => (
                <Card.Root key={item.title} bg="design.bg.surface" borderWidth="1px" borderColor="design.border.default" rounded="card">
                  <Card.Body p="5" gap="2">
                    <Heading fontFamily="display" fontSize="xl">{item.title}</Heading>
                    <Text fontSize="sm" color="design.fg.muted" lineHeight="1.8">{item.copy}</Text>
                  </Card.Body>
                </Card.Root>
              ))}
            </SimpleGrid>
          </SectionFrame>
        </Stack>
      </Container>
    </Box>
  );
};

export default DesignSystem;
